import { createFileRoute } from '@tanstack/react-router'
import { ProductFrame } from '@/components/marketing/product-frame'
import { KeywordPiece } from '@/components/marketing/ui-pieces'
import { FeaturePage } from '@/components/feature-page'

export const Route = createFileRoute('/_site/for/ecommerce')({
  component: EcommercePage,
  head: () => ({
    meta: [
      { title: 'Ecommerce Uptime Monitoring — Protect Every Sale | Uptrack' },
      { name: 'description', content: 'Monitor your store 24/7 with 30s checks. Catch checkout failures, SSL issues, cart errors before customers leave.' },
      { property: 'og:title', content: 'Ecommerce Uptime Monitoring — Protect Every Sale | Uptrack' },
      { property: 'og:description', content: 'Monitor your online store with 30s checks. Detect checkout failures and cart errors before customers leave. Free plan, or paid plans from $3/mo.' },
      { property: 'og:image', content: 'https://uptrack.app/og/for-ecommerce.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/for/ecommerce' }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://uptrack.app' },
            { '@type': 'ListItem', position: 2, name: 'For Ecommerce', item: 'https://uptrack.app/for/ecommerce' },
          ],
        }),
      },
    ],
  }),
})

function EcommercePage() {
  return (
    <FeaturePage
      section="Ecommerce uptime monitoring"
      eyebrow="For · Ecommerce uptime monitoring"
      title="Every minute of downtime costs you sales."
      intro="Amazon loses $220,000 per minute during outages. Your store can't afford downtime either. Uptrack checks your storefront, checkout, and payment pages every 30 seconds — so you know before your customers do."
      featuresHeading="Protect every step of the buying journey"
      features={[
        { title: 'Checkout page monitoring', desc: 'Monitor your cart and checkout flow separately. Know quickly if the page that makes you money goes down.' },
        { title: '30-second detection', desc: "Most tools check every 5 minutes — that's 5 minutes of lost orders. Uptrack detects outages within 30 seconds and alerts you once several locations confirm them." },
        { title: 'Status pages for trust', desc: 'Give customers a branded status page. Transparency builds trust and reduces support tickets during incidents.' },
        { title: 'SSL monitoring', desc: 'Expired SSL on payment pages kills conversions and triggers browser warnings. Get alerted days before expiry.' },
        { title: 'Keyword monitoring', desc: 'Detect "Error establishing database connection" or cart errors by checking for specific text on your pages.' },
        { title: 'Response time tracking', desc: 'Slow pages lose sales. Track response times and spot performance degradation before it impacts revenue.' },
      ]}
      why={{ heading: "Don't let downtime eat your revenue", body: "A customer adds items to their cart and clicks checkout — but your payment page is down. They leave and buy from a competitor. With Uptrack, you'd know in 30 seconds, fix it in minutes, and keep that sale. Set up monitors for your storefront, cart, checkout, and API in under a minute." }}
      cta={{ heading: '30-second checks on every plan', body: 'Monitor storefront, checkout, and payment pages. Start free, or choose a plan for more.' }}
      visual={
        <ProductFrame url="uptrack.app/dashboard/monitors">
          <KeywordPiece />
        </ProductFrame>
      }
    />
  )
}
